import { useGetCrmDataQuery } from '../../../../generated/graphql';
import { CrmSegment } from './CrmSegment.enum';
import { SubscribedTable } from './SubscribedTable';
import { useCrmStore } from './CreatorCrm.store';
import { OtpTable } from './OtpTable';
import { LeadsTable } from './LeadsTable';
import { CreatorCrmLayout } from './CreatorCrm.layout';
import {
  getLeadsUsers,
  getOtpUsers,
  getSubscribedUsers,
} from './CreatorCrm.api';
import { createRef, useEffect, useState } from 'react';
import { debounce } from 'lodash';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import { isOtpCreator } from '@client/new/common/offering.helpers';

const ALLOWED_SEGMENTS = [
  CrmSegment.Subscribed,
  CrmSegment.ExclusiveBuyers,
  CrmSegment.Leads,
];

type Query = {
  page: number;
  pageSize: number;
};
const UsersTab = ({ influencerId }: { influencerId: number }) => {
  const tableRef = createRef();
  const crmStore = useCrmStore();
  const crmQuery = useGetCrmDataQuery({
    variables: {
      creatorId: influencerId,
    },
  });

  const filters = useCrmStore((s) => s.filters);
  const searchTerm = useCrmStore((s) => s.searchTerm);
  const refetchTableData = () => {
    tableRef.current && (tableRef.current as any).onQueryChange();
  };
  useEffect(refetchTableData, [filters]);
  useEffect(debounce(refetchTableData, 200), [searchTerm]);
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    crmStore.setActiveSegment(
      ALLOWED_SEGMENTS.includes(hash as CrmSegment)
        ? (hash as CrmSegment)
        : CrmSegment.Subscribed,
    );
  }, []);

  useEffect(() => {
    const isOtpOnly =
      isOtpCreator(crmQuery.data?.creator?.offering) &&
      (crmQuery.data?.creator?.offering || []).length === 1;
    crmStore.setActiveSegment(
      isOtpOnly ? CrmSegment.ExclusiveBuyers : CrmSegment.Subscribed,
    );
  }, [crmQuery]);
  const [totalResults, setTotalResults] = useState(0);

  if (crmQuery.loading) {
    return <div>Loading...</div>;
  }

  const Tables = {
    [CrmSegment.Subscribed]: {
      component: SubscribedTable,
      async getData(query: Query) {
        const page = query.page + 1;
        const result = await getSubscribedUsers(influencerId, {
          pagination: {
            page,
            pageSize: query.pageSize,
          },
          classification: crmStore.filters.classification,
          status: crmStore.filters.subscription,
          type: crmStore.filters.type,
          searchTerm: crmStore.searchTerm,
          label: crmStore.filters.additional,
        });

        setTotalResults(result.meta.totalItems);

        return {
          data: result.records,
          page: page - 1,
          totalCount: result.meta.totalItems,
        };
      },
    },
    [CrmSegment.ExclusiveBuyers]: {
      component: OtpTable,
      async getData(query: Query) {
        const page = query.page + 1;
        const result = await getOtpUsers(influencerId, {
          pagination: {
            page,
            pageSize: query.pageSize,
          },
          programId: crmStore.filters.programId,
          searchTerm: crmStore.searchTerm,
          status: crmStore.filters.subscription,
        });

        setTotalResults(result.meta.totalItems);

        return {
          data: result.records,
          page: page - 1,
          totalCount: result.meta.totalItems,
        };
      },
    },
    [CrmSegment.Leads]: {
      component: LeadsTable,
      async getData(query: Query) {
        const page = query.page + 1;
        const result = await getLeadsUsers(influencerId, {
          pagination: {
            page,
            pageSize: query.pageSize,
          },
          searchTerm: crmStore.searchTerm,
          source: crmStore.filters.leadsSource,
        });

        setTotalResults(result.meta.totalItems);

        return {
          data: result.records,
          page: page - 1,
          totalCount: result.meta.totalItems,
        };
      },
    },
  };

  // @ts-ignore
  const selectedTable = Tables[crmStore.segment || CrmSegment.Subscribed];

  return (
    <div className="overflow-scroll">
      <selectedTable.component
        tableRef={tableRef}
        data={selectedTable.getData}
        totalResults={totalResults}
        creator={crmQuery.data?.creator}
      />
    </div>
  );
};

export const CreatorCrmPage = () => {
  const influencerId = useInfluencerId();

  return (
    <CreatorCrmLayout influencerId={influencerId}>
      <UsersTab influencerId={influencerId} />
    </CreatorCrmLayout>
  );
};
